import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { CHART_COLORS, AREAS, STATUS, labelOf } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { Trophy, Users, Activity, X, Sprout, Eye } from "lucide-react";
import SeedDetailModal from "@/components/SeedDetailModal";
import ModuleHelp from "@/components/help/ModuleHelp";

const tooltipStyle = {
  backgroundColor: "#0A1118",
  border: "1px solid #2A3A4A",
  borderRadius: 4,
  fontSize: 12,
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const Panel = ({ title, hint, children, testid }) => (
  <div className="card-mil p-5" data-testid={testid}>
    <div className="mb-4">
      <h3 className="font-display font-bold text-base">{title}</h3>
      {hint && <p className="text-xs text-[var(--text-muted)] mt-1">{hint}</p>}
    </div>
    {children}
  </div>
);

export default function Produtividade() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [selectedUser, setSelectedUser] = useState(null); // {user_id, name}
  const [userSeeds, setUserSeeds] = useState([]);
  const [seedsLoading, setSeedsLoading] = useState(false);
  const [openSeed, setOpenSeed] = useState(null);

  useEffect(() => {
    api.get("/dashboard/user-productivity").then((r) => setData(r.data)).catch((e) => setErr(e.message));
  }, []);

  const openUserDrilldown = async (user) => {
    if (!user?.user_id) return;
    setSelectedUser(user);
    setUserSeeds([]);
    setSeedsLoading(true);
    try {
      const { data: seeds } = await api.get("/seeds", {
        params: { inserido_por: user.user_id, limit: 500 },
      });
      setUserSeeds(seeds || []);
    } catch (e) {
      // Silenciosamente captura erro de listagem mas mantém o modal aberto
      setUserSeeds([]);
    } finally {
      setSeedsLoading(false);
    }
  };

  const closeUserDrilldown = () => {
    setSelectedUser(null);
    setUserSeeds([]);
  };

  // Aggregate timeline data per day (sum across users)
  const aggregatedTimeline = useMemo(() => {
    if (!data?.timeline) return [];
    const m = {};
    data.timeline.forEach((t) => {
      m[t.date] = (m[t.date] || 0) + t.value;
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([date, value]) => ({ date, value }));
  }, [data]);

  if (err) return <div className="text-[var(--danger)]" data-testid="produtividade-error">{err}</div>;
  if (!data) return <div className="font-mono text-sm text-[var(--text-muted)]" data-testid="produtividade-loading">CARREGANDO…</div>;

  const rows = data.rows;
  const rowsBarras = rows.map((r) => ({ name: r.name, value: r.total })).slice(0, 10);
  const pieData = rows.slice(0, 8).map((r) => ({ name: r.name, value: r.total }));
  const rankCadastros = [...rows].sort((a, b) => b.total - a.total).slice(0, 5);
  const rankAprovacoes = [...rows].sort((a, b) => b.aprovadas - a.aprovadas).slice(0, 5);

  return (
    <div className="space-y-6" data-testid="produtividade-page">
      <div>
        <div className="label-mil text-[var(--gold)]">Produtividade</div>
        <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-3">Contribuições por Pesquisador <ModuleHelp module="produtividade" /></h1>
        <p className="text-[var(--text-muted)] text-sm mt-2">
          {data.totals.users_with_contribution} pesquisadores ativos · {data.totals.seeds_total} sementes registradas
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-mil p-5 gold-accent-top">
          <div className="label-mil flex items-center gap-2"><Users size={12} className="text-[var(--gold)]" /> Pesquisadores Contribuindo</div>
          <div className="kpi-num mt-2">{data.totals.users_with_contribution}</div>
        </div>
        <div className="card-mil p-5 gold-accent-top">
          <div className="label-mil flex items-center gap-2"><Activity size={12} className="text-[var(--gold)]" /> Sementes Totais</div>
          <div className="kpi-num mt-2">{data.totals.seeds_total}</div>
        </div>
        <div className="card-mil p-5 gold-accent-top">
          <div className="label-mil flex items-center gap-2"><Trophy size={12} className="text-[var(--gold)]" /> Top Contribuinte</div>
          <div className="kpi-num mt-2 text-2xl truncate">{rankCadastros[0]?.name || "—"}</div>
          <div className="text-xs text-[var(--text-muted)] font-mono mt-1">{rankCadastros[0]?.total || 0} sementes</div>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Fontes cadastradas por usuário" testid="chart-bar">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={rowsBarras} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid stroke="#2A3A4A" strokeDasharray="2 4" horizontal={false} />
              <XAxis type="number" stroke="#8BA3B8" fontSize={10} />
              <YAxis type="category" dataKey="name" width={150} stroke="#8BA3B8" fontSize={10} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="#C5A028" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Status por usuário (empilhado)" testid="chart-stacked" hint="Pendentes, Aprovadas e Rejeitadas por contribuinte">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={rows.slice(0, 10).map((r) => ({ name: r.name, Pendentes: r.pendentes, Aprovadas: r.aprovadas, Rejeitadas: r.rejeitadas }))} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid stroke="#2A3A4A" strokeDasharray="2 4" horizontal={false} />
              <XAxis type="number" stroke="#8BA3B8" fontSize={10} />
              <YAxis type="category" dataKey="name" width={150} stroke="#8BA3B8" fontSize={10} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 10, color: "#8BA3B8" }} />
              <Bar dataKey="Pendentes" stackId="a" fill="#F59E0B" />
              <Bar dataKey="Aprovadas" stackId="a" fill="#10B981" />
              <Bar dataKey="Rejeitadas" stackId="a" fill="#EF4444" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Distribuição percentual" testid="chart-pie" hint="Participação por pesquisador (top 8)">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={100} paddingAngle={2}>
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={CHART_COLORS[pieData.indexOf(entry) % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 10, color: "#8BA3B8" }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Linha do tempo (90d)" testid="chart-timeline" hint="Sementes inseridas por dia">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={aggregatedTimeline}>
              <CartesianGrid stroke="#2A3A4A" strokeDasharray="2 4" />
              <XAxis dataKey="date" stroke="#8BA3B8" fontSize={9} />
              <YAxis stroke="#8BA3B8" fontSize={10} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="value" stroke="#38BDF8" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Rankings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title="Ranking · Cadastros" testid="ranking-cadastros">
          <ol className="space-y-2">
            {rankCadastros.map((r, idx) => (
              <li key={r.user_id || idx}>
                <button
                  type="button"
                  onClick={() => openUserDrilldown(r)}
                  disabled={!r.user_id}
                  className="w-full flex items-center gap-3 text-sm hover:bg-[var(--surface-2)] rounded px-2 py-1 text-left disabled:cursor-default disabled:hover:bg-transparent"
                  data-testid={`rank-cadastros-${r.user_id || idx}`}
                >
                  <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${idx === 0 ? "bg-[var(--gold)] text-[var(--bg)]" : "bg-[var(--surface-2)] text-[var(--text-muted)]"}`}>
                    {idx + 1}
                  </span>
                  <span className="flex-1 truncate">{r.name}</span>
                  <span className="badge badge-gold">{r.total}</span>
                </button>
              </li>
            ))}
          </ol>
        </Panel>

        <Panel title="Ranking · Aprovações" testid="ranking-aprovacoes">
          <ol className="space-y-2">
            {rankAprovacoes.map((r, idx) => (
              <li key={r.user_id || idx}>
                <button
                  type="button"
                  onClick={() => openUserDrilldown(r)}
                  disabled={!r.user_id}
                  className="w-full flex items-center gap-3 text-sm hover:bg-[var(--surface-2)] rounded px-2 py-1 text-left disabled:cursor-default disabled:hover:bg-transparent"
                  data-testid={`rank-aprovacoes-${r.user_id || idx}`}
                >
                  <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${idx === 0 ? "bg-[var(--gold)] text-[var(--bg)]" : "bg-[var(--surface-2)] text-[var(--text-muted)]"}`}>
                    {idx + 1}
                  </span>
                  <span className="flex-1 truncate">{r.name}</span>
                  <span className="badge badge-success">{r.aprovadas}</span>
                </button>
              </li>
            ))}
          </ol>
        </Panel>
      </div>

      {/* Detailed table */}
      <div className="card-mil overflow-hidden" data-testid="produtividade-table-wrap">
        <div className="p-4 border-b border-[var(--border)]">
          <h3 className="font-display font-bold">Tabela Detalhada por Usuário</h3>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="produtividade-table">
            <thead>
              <tr>
                {["Nome", "E-mail", "Perfil", "Total", "Pendentes", "Revisões", "Aprovadas", "Rejeitadas", "7d", "30d", "Última contribuição"].map((h) => (
                  <th key={h} className="px-4 py-3 label-mil border-b border-[var(--border)] text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={r.user_id || i}
                  className={`border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)] ${r.user_id ? "cursor-pointer" : ""}`}
                  onClick={() => r.user_id && openUserDrilldown(r)}
                  data-testid={`row-${r.user_id || "anon"}`}
                >
                  <td className="px-4 py-3 font-semibold">{r.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--text-muted)]">{r.email}</td>
                  <td className="px-4 py-3 text-xs uppercase">{r.perfil}</td>
                  <td className="px-4 py-3"><span className="badge badge-gold">{r.total}</span></td>
                  <td className="px-4 py-3"><span className="badge badge-warn">{r.pendentes}</span></td>
                  <td className="px-4 py-3"><span className="badge badge-info">{r.revisadas}</span></td>
                  <td className="px-4 py-3"><span className="badge badge-success">{r.aprovadas}</span></td>
                  <td className="px-4 py-3"><span className="badge badge-danger">{r.rejeitadas}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{r.last_7d}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.last_30d}</td>
                  <td className="px-4 py-3 font-mono text-[10px] text-[var(--text-muted)]">{fmtDate(r.ultima_contribuicao)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={11} className="text-center p-8 text-[var(--text-muted)] text-sm">Sem contribuições</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drilldown modal — sementes do usuário selecionado */}
      {selectedUser && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeUserDrilldown}
          data-testid="user-drilldown-overlay"
        >
          <div
            className="card-mil w-full max-w-5xl my-8 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="user-drilldown-modal"
          >
            <div className="p-5 border-b border-[var(--border)] flex items-start justify-between gap-3 gold-accent-top">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sprout size={14} className="text-[var(--gold)]" />
                  <span className="label-mil text-[var(--gold)]">Sementes inseridas</span>
                </div>
                <h2 className="font-display font-bold text-xl text-[var(--text)] leading-tight">{selectedUser.name}</h2>
                <p className="text-xs text-[var(--text-muted)] mt-1 font-mono">
                  {seedsLoading ? "CARREGANDO…" : `${userSeeds.length} REGISTROS`}
                  {selectedUser.email && ` · ${selectedUser.email}`}
                </p>
              </div>
              <button
                onClick={closeUserDrilldown}
                className="p-2 hover:bg-[var(--surface-2)] rounded text-[var(--text-muted)] hover:text-[var(--text)]"
                data-testid="user-drilldown-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-auto scrollbar-thin">
              {seedsLoading ? (
                <div className="font-mono text-xs text-[var(--text-muted)] text-center py-12">CARREGANDO…</div>
              ) : userSeeds.length === 0 ? (
                <div className="p-12 text-center text-[var(--text-muted)] text-sm">
                  Este pesquisador ainda não inseriu sementes.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[var(--surface)] z-10">
                    <tr>
                      <th className="px-4 py-2 label-mil text-left">Título</th>
                      <th className="px-4 py-2 label-mil text-left">Área</th>
                      <th className="px-4 py-2 label-mil text-left">Classif.</th>
                      <th className="px-4 py-2 label-mil text-left">Status</th>
                      <th className="px-4 py-2 label-mil text-left">Data</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {userSeeds.map((s) => {
                      const statusDef = STATUS.find((x) => x.value === s.status_validacao);
                      return (
                        <tr
                          key={s.seed_id}
                          className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)] cursor-pointer"
                          onClick={() => setOpenSeed(s.seed_id)}
                          data-testid={`user-seed-row-${s.seed_id}`}
                        >
                          <td className="px-4 py-2 max-w-[400px]">
                            <div className="font-semibold truncate">{s.titulo_insumo}</div>
                            <div className="text-[10px] font-mono text-[var(--text-muted)] truncate">{s.seed_id}</div>
                          </td>
                          <td className="px-4 py-2 text-xs">{labelOf(AREAS, s.area_tematica_principal)}</td>
                          <td className="px-4 py-2"><span className="badge badge-gold">{s.classificacao_semente}</span></td>
                          <td className="px-4 py-2">
                            <span className={`badge ${statusDef?.className || "badge-muted"}`}>
                              {statusDef?.label || s.status_validacao || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-2 font-mono text-[10px] text-[var(--text-muted)]">{fmtDate(s.data_cadastro || s.created_at)}</td>
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenSeed(s.seed_id); }}
                              className="btn-ghost text-xs flex items-center gap-1"
                              data-testid={`user-seed-open-${s.seed_id}`}
                            >
                              <Eye size={11} /> Ver
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="p-3 border-t border-[var(--border)] flex justify-end items-center gap-2 bg-[var(--bg)]/40">
              <button onClick={closeUserDrilldown} className="btn-ghost text-xs">Fechar</button>
            </div>
          </div>
        </div>
      )}

      <SeedDetailModal seedId={openSeed} onClose={() => setOpenSeed(null)} />
    </div>
  );
}
